// Runtime brand (name, hosts, logo). In production the server injects
// `window.__BRAND__` into index.html from its BRAND_* / PUBLIC_* env vars
// (server/src/brand.ts); in Vite dev there is no injection and the Happy Pony
// defaults apply. Hard-coding a brand string or host anywhere else in the
// client is a bug: read it from here.

export type Brand = {
  name: string;
  nameZhCN: string | null;
  webUrl: string;
  apiUrl: string;
  docsUrl: string;
  supportEmail: string;
  logoUrl: string | null;
};

export const DEFAULT_BRAND: Brand = {
  name: 'Happy Pony',
  nameZhCN: '快乐小马',
  webUrl: 'https://www.happypony.io',
  apiUrl: 'https://api.happypony.io',
  docsUrl: 'https://docs.happypony.io',
  supportEmail: 'support@happypony.io',
  logoUrl: null,
};

type BrandWindow = { __BRAND__?: Partial<Record<keyof Brand, unknown>> };

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

export function readBrand(win: BrandWindow = globalThis as BrandWindow): Brand {
  const b = win.__BRAND__ ?? {};
  const name = str(b.name);
  return {
    name: name ?? DEFAULT_BRAND.name,
    nameZhCN: str(b.nameZhCN) ?? (name ? null : DEFAULT_BRAND.nameZhCN),
    webUrl: (str(b.webUrl) ?? DEFAULT_BRAND.webUrl).replace(/\/+$/, ''),
    apiUrl: (str(b.apiUrl) ?? DEFAULT_BRAND.apiUrl).replace(/\/+$/, ''),
    docsUrl: (str(b.docsUrl) ?? DEFAULT_BRAND.docsUrl).replace(/\/+$/, ''),
    supportEmail: str(b.supportEmail) ?? DEFAULT_BRAND.supportEmail,
    logoUrl: str(b.logoUrl) ?? null,
  };
}

export const BRAND: Brand = readBrand();

// Display name for a locale: the Chinese name when the brand has one.
export function brandName(locale: string, brand: Brand = BRAND): string {
  return locale === 'zh-CN' && brand.nameZhCN ? brand.nameZhCN : brand.name;
}

// The docs page takes ?lang=en|zh-CN; open it in the portal's current language.
export function apiDocsUrl(locale: string, brand: Brand = BRAND): string {
  return `${brand.docsUrl}/?lang=${locale === 'zh-CN' ? 'zh-CN' : 'en'}`;
}
